import * as React from "react";
import {
  Button,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Slide,
  TextField,
  Theme,
  withStyles,
} from "@material-ui/core";
import { API } from "aws-amplify";
import { TransitionProps } from "@material-ui/core/transitions";
import { LoadingSpinner } from "../../../../../../utils/LoadingSpinner";
import PharmacyProfileResult from "./PharmacyProfileResult";
import { Alert } from "@material-ui/lab";

const styles = ({ spacing }: Theme) =>
  createStyles({
    buttonWrapper: {
      backgroundColor: "#00cc00",
      marginTop: spacing(1),
    },
  });

interface IPharmacyProfileState {
  formSubmitted: boolean;
  message: string;
  open: boolean;
  personId: string;
  resultSuccessful: boolean;
  showLoadingSpinner: boolean;
  statusReasonType: string;
  response: string;
}

const STATUS_REASON_OPTIONS = [
  { value: "CLOSED_ON_CUSTOMER_REQUEST", label: "CLOSED ON CUSTOMER REQUEST" },
  { value: "DELETED_AS_FRAUD", label: "DELETED AS FRAUD" },
  { value: "DELETED_AS_DUPLICATE", label: "DELETED AS DUPLICATE" },
  { value: "DELETED_FOR_GRADUATION", label: "DELETED FOR GRADUATION" },
  { value: "STALE_HEALTH_PROFILE", label: "STALE HEALTH PROFILE" },
  { value: "VERIFIED_BY_KBQ", label: "VERIFIED BY KBQ (UNLOCK ACCOUNT)" },
];

const GENERIC_ERROR_MESSAGE = "The request failed before reaching the server. Please try again.";

const TransitionConst = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

class PharmacyProfileSection extends React.Component<
  any,
  IPharmacyProfileState
> {
  constructor(props: any) {
    super(props);
    this.state = {
      formSubmitted: false,
      message: "",
      open: false,
      personId: "",
      response: "",
      resultSuccessful: false,
      showLoadingSpinner: false,
      statusReasonType: "",
    };

    this.handlePersonId = this.handlePersonId.bind(this);
    this.handleStatusReasonType = this.handleStatusReasonType.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleClose = this.handleClose.bind(this);
  }

  public render() {
    // @ts-ignore
    return (
      <Grid item xs={12}>
        <Grid
          container
          direction="column"
          justify="center"
          alignItems="center"
          spacing={3}
        >
          {this.state.showLoadingSpinner && <LoadingSpinner />}

          <Alert severity="info">
            <p>
              1. Make sure the profile (with the personId below) is enrolled to
              a pharmacy program.
            </p>
          </Alert>

          <Grid item xs>
            <TextField
              style={{ width: 225 }}
              label="Person Id (Required)"
              variant="outlined"
              value={this.state.personId}
              onChange={this.handlePersonId}
              disabled={this.state.formSubmitted}
            />
          </Grid>

          <Grid item xs>
            <FormControl disabled={this.state.formSubmitted}>
              <InputLabel>StatusReasonType (Required)</InputLabel>
              <Select
                value={this.state.statusReasonType}
                style={{ width: 280 }}
                onChange={this.handleStatusReasonType}
              >
                {STATUS_REASON_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <em>{option.label}</em>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs>
            <Button
              variant="outlined"
              color="default"
              onClick={this.handleSubmit}
              disabled={this.state.formSubmitted}
            >
              Submit
            </Button>
          </Grid>

          <Grid item xs={12}>
            {this.state.formSubmitted && <LoadingSpinner />}
            {this.state.resultSuccessful && (
              <PharmacyProfileResult
                resultSuccessful={this.state.resultSuccessful}
                response={this.state.response}
              />
            )}
          </Grid>

          {this.state.message && (
            <Dialog
              open={this.state.open}
              TransitionComponent={TransitionConst}
              keepMounted
              aria-labelledby="alert-dialog-slide-title"
              aria-describedby="alert-dialog-slide-description"
              onClose={this.handleClose}
            >
              <DialogTitle id="alert-dialog-slide-title">
                {"Exception occurred when updating profile status"}
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="alert-dialog-slide-description">
                  {this.state.message}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={this.handleClose} color="primary">
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          )}
        </Grid>
      </Grid>
    );
  }

  public handleClose() {
    this.setState({ open: false });
  }

  public handlePersonId(event: React.ChangeEvent<HTMLTextAreaElement>) {
    this.setState({ personId: event.target.value, resultSuccessful: false });
  }

  public handleStatusReasonType(event: React.ChangeEvent<HTMLInputElement>) {
    this.setState({ statusReasonType: event.target.value, resultSuccessful: false });
  }

  private handleValidation() {
    let formIsValid = true;
    if (!this.state.personId) {
      alert("Please enter a person id");
      formIsValid = false;
    } else if (
      !this.state.personId.match(
        "^amzn[1-9]+[0-9]*.([A-Za-z0-9]+.)+A[A-Z0-9]*$"
      )
    ) {
      alert("Please enter a valid person id.");
      formIsValid = false;
    } else if (!this.state.statusReasonType) {
      alert("Please select a StatusReasonType.");
      formIsValid = false;
    }
    return formIsValid;
  }

  private handleSubmit() {
    if (this.handleValidation()) {
      this.setState({ formSubmitted: true });
      this.setState({ resultSuccessful: false });

      const apiName = "ApaqPortalAPI";
      const path = "/pharmacy-customer/update/update-profile";
      const { personId, statusReasonType } = this.state;
      const config = {
        body: { personId, statusReasonType },
        headers: {},
      };
      const label = STATUS_REASON_OPTIONS.find((o) => o.value === statusReasonType)?.label ?? statusReasonType;

      API.post(apiName, path, config)
        .then((response) => {
          console.log(response);
          this.setState({ resultSuccessful: true });
          this.setState({ formSubmitted: false });
          this.setState({
            response: `${label} was successful for ${personId}`,
          });
        })
        .catch((error) => {
          console.log(error);
          this.setState({ open: true });
          this.setState({ formSubmitted: false });
          this.setState({ message: error?.response?.data?.message || GENERIC_ERROR_MESSAGE });
        });
    }
  }
}

export default withStyles(styles)(PharmacyProfileSection);
